import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, getApiList } from '../api';
import { useNavigate } from 'react-router-dom';

function Stores() {
  const navigate = useNavigate();

  const emptyForm = {
    name: '',
    phone: '',
    address: '',
    owner_name: '',
    owner_phone: '',
    map_address: '',
    notes: '',
    status: 'active'
  };

  const [stores, setStores] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);

  const [showForm, setShowForm] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);

  const [selectedStore, setSelectedStore] = useState(null);
  const [editingStore, setEditingStore] = useState(null);
  const [statementStore, setStatementStore] = useState(null);
  const [statement, setStatement] = useState(null);
  const [statementFrom, setStatementFrom] = useState('');
  const [statementTo, setStatementTo] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [restoringId, setRestoringId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchStores();
  }, [showDeleted]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await apiFetch(`/stores${showDeleted ? '?include_deleted=1' : ''}`);
      if (!res) return;

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'فشل جلب المحلات');
        return;
      }

      setStores(getApiList(data, 'stores'));
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب المحلات');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
      .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
      .replace(/[\u064B-\u065F\u0610-\u061A]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    if (!Number.isFinite(number)) return '0';

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const formatDate = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getStatusInfo = (status) => {
    const map = {
      active: {
        label: 'نشط',
        icon: '✅',
        className: 'bg-green-100 text-green-700 border-green-200'
      },
      paused: {
        label: 'متوقف مؤقتاً',
        icon: '⏸️',
        className: 'bg-yellow-100 text-yellow-700 border-yellow-200'
      },
      follow_up: {
        label: 'يحتاج متابعة',
        icon: '📌',
        className: 'bg-orange-100 text-orange-700 border-orange-200'
      },
      deleted: {
        label: 'محذوف',
        icon: '🗑️',
        className: 'bg-red-100 text-red-700 border-red-200'
      }
    };

    return map[status || 'active'] || map.active;
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن',
      collection: 'قبض',
      transfer: 'تحويل',
      expense: 'مصروف',
      company_settlement: 'تسديد شركة'
    };

    return labels[type] || type || '-';
  };

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄',
      collection: '💵',
      transfer: '↔️',
      expense: '💸',
      company_settlement: '🏢'
    };

    return icons[type] || '🧾';
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleEditChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
  };

  const buildPayload = (source) => ({
    name: String(source.name || '').trim(),
    phone: String(source.phone || '').trim(),
    address: String(source.address || '').trim(),
    owner_name: String(source.owner_name || '').trim(),
    owner_phone: String(source.owner_phone || '').trim(),
    map_address: String(source.map_address || '').trim(),
    notes: String(source.notes || '').trim(),
    status: source.status || 'active'
  });

  const resetForm = () => setForm(emptyForm);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError('اسم المحل مطلوب');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const res = await apiFetch('/stores', {
        method: 'POST',
        body: JSON.stringify(buildPayload(form))
      });

      if (!res) return;

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'فشل إنشاء المحل');
        return;
      }

      setSuccess('تم إنشاء المحل بنجاح');
      resetForm();
      await fetchStores();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء إنشاء المحل');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (store) => {
    setSelectedStore(null);
    setEditingStore(store);
    setEditForm({
      name: store.name || '',
      phone: store.phone || '',
      address: store.address || '',
      owner_name: store.owner_name || '',
      owner_phone: store.owner_phone || '',
      map_address: store.map_address || '',
      notes: store.notes || '',
      status: store.status || 'active'
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!editingStore) return;

    if (!editForm.name.trim()) {
      setError('اسم المحل مطلوب');
      return;
    }

    const confirmed = window.confirm(`هل تريد حفظ تعديل بيانات المحل "${editForm.name}"؟`);
    if (!confirmed) return;

    try {
      setEditSaving(true);
      setError('');
      setSuccess('');

      const res = await apiFetch(`/stores/${editingStore.id}`, {
        method: 'PUT',
        body: JSON.stringify(buildPayload(editForm))
      });

      if (!res) return;

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'فشل تعديل بيانات المحل');
        return;
      }

      setSuccess('تم تعديل بيانات المحل بنجاح');
      setEditingStore(null);
      await fetchStores();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تعديل بيانات المحل');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (store) => {
    const confirmed = window.confirm(
      `هل أنت متأكد من تعطيل المحل "${store.name}"؟\nسيتم حذفه حذفاً ناعماً وليس حذفه نهائياً.`
    );

    if (!confirmed) return;

    try {
      setDeletingId(store.id);
      setError('');
      setSuccess('');

      const res = await apiFetch(`/stores/${store.id}`, {
        method: 'DELETE'
      });

      if (!res) return;

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'فشل تعطيل المحل');
        return;
      }

      setSuccess('تم تعطيل المحل بنجاح');
      await fetchStores();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تعطيل المحل');
    } finally {
      setDeletingId('');
    }
  };

  const handleRestore = async (store) => {
    const confirmed = window.confirm(`هل تريد استرجاع المحل "${store.name}"؟`);
    if (!confirmed) return;

    try {
      setRestoringId(store.id);
      setError('');
      setSuccess('');

      const res = await apiFetch(`/stores/${store.id}/restore`, {
        method: 'PUT'
      });

      if (!res) return;

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'فشل استرجاع المحل');
        return;
      }

      setSuccess('تم استرجاع المحل بنجاح');
      await fetchStores();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء استرجاع المحل');
    } finally {
      setRestoringId('');
    }
  };

  const openStatement = async (store, keepDates = false) => {
    setSelectedStore(null);
    setStatementStore(store);

    const from = keepDates ? statementFrom : '';
    const to = keepDates ? statementTo : '';

    if (!keepDates) {
      setStatementFrom('');
      setStatementTo('');
    }

    await fetchStatement(store.id, from, to);
  };

  const fetchStatement = async (storeId, from = statementFrom, to = statementTo) => {
    try {
      setStatementLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);

      const url = `/stores/${storeId}/statement${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await apiFetch(url);

      if (!res) return;

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || data.error || 'فشل جلب كشف حساب المحل');
        return;
      }

      setStatement(data);
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب كشف حساب المحل');
    } finally {
      setStatementLoading(false);
    }
  };

  const closeStatement = () => {
    setStatementStore(null);
    setStatement(null);
    setStatementFrom('');
    setStatementTo('');
  };

  const filteredStores = useMemo(() => {
    const query = normalizeArabic(searchText);
    const terms = query ? query.split(' ').filter(Boolean) : [];

    const visibleStatus = (store) => store.deleted_at ? 'deleted' : (store.status || 'active');

    const scoreStore = (store) => {
      const fields = [
        store.name,
        store.phone,
        store.owner_name,
        store.owner_phone,
        store.address,
        store.map_address,
        store.notes,
        visibleStatus(store),
        getStatusInfo(visibleStatus(store)).label,
        store.id,
        store.balance,
        store.total_debt
      ];

      const text = normalizeArabic(fields.filter(Boolean).join(' | '));
      const name = normalizeArabic(store.name);
      const phone = normalizeArabic(store.phone || store.owner_phone);
      const owner = normalizeArabic(store.owner_name);

      if (!query) return 10;
      if (!text) return 0;
      if (name.startsWith(query)) return 120;
      if (phone.startsWith(query)) return 110;
      if (owner.startsWith(query)) return 100;
      if (text.startsWith(query)) return 90;
      if (text.includes(query)) return 70;
      if (terms.length && terms.every(term => text.includes(term))) return 60;

      return 0;
    };

    return stores
      .filter(store => statusFilter === 'all' || visibleStatus(store) === statusFilter)
      .map(store => ({
        store,
        score: scoreStore(store)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return new Date(b.store.created_at || 0) - new Date(a.store.created_at || 0);
      })
      .map(item => item.store);
  }, [stores, searchText, statusFilter]);

  const stats = useMemo(() => {
    const statusOf = (store) => store.deleted_at ? 'deleted' : (store.status || 'active');
    const active = stores.filter(store => statusOf(store) === 'active').length;
    const paused = stores.filter(store => statusOf(store) === 'paused').length;
    const followUp = stores.filter(store => statusOf(store) === 'follow_up').length;
    const deleted = stores.filter(store => statusOf(store) === 'deleted').length;
    const totalDebt = stores.reduce((sum, store) => sum + Number(store.total_debt || store.balance || 0), 0);

    return {
      active,
      paused,
      followUp,
      deleted,
      totalDebt
    };
  }, [stores]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                🏪 إدارة المحلات
              </h1>
              <p className="text-orange-100 mt-3 text-sm md:text-base">
                إضافة المحلات، تعديل البيانات، كشف الحساب، الحالة، والديون
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
              <MiniStat label="نشطة" value={stats.active} />
              <MiniStat label="متابعة" value={stats.followUp} />
              <MiniStat label="متوقفة" value={stats.paused} />
              <MiniStat label="الدين" value={formatNumber(stats.totalDebt)} />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mb-6">
            ⚠️ {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl px-4 py-3 mb-6">
            ✅ {success}
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🔎 البحث الذكي في المحلات
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                ابحث باسم المحل، رقم الهاتف، المالك، العنوان، الملاحظات، الرصيد، أو الحالة
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowForm(!showForm)}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 py-3 rounded-xl"
              >
                {showForm ? 'إخفاء النموذج' : '➕ إضافة محل'}
              </button>

              <button
                onClick={() => setShowDeleted(!showDeleted)}
                className={`${showDeleted ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'} font-bold px-5 py-3 rounded-xl`}
              >
                {showDeleted ? 'إخفاء المعطلة' : 'عرض المعطلة'}
              </button>

              <button
                onClick={fetchStores}
                disabled={loading}
                className="bg-gray-800 hover:bg-black disabled:opacity-50 text-white font-bold px-5 py-3 rounded-xl"
              >
                {loading ? 'جاري التحديث...' : '🔄 تحديث'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="اكتب أول حرف أو رقم هاتف أو أي معلومة..."
              className="lg:col-span-3 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="all">كل الحالات</option>
              <option value="active">نشط</option>
              <option value="follow_up">يحتاج متابعة</option>
              <option value="paused">متوقف مؤقتاً</option>
              <option value="deleted">محذوف</option>
            </select>
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">
              ➕ إضافة محل جديد
            </h2>

            <StoreForm
              form={form}
              onChange={handleChange}
              onSubmit={handleSubmit}
              saving={saving}
              submitLabel="✅ حفظ المحل"
              onCancel={resetForm}
              cancelLabel="مسح الحقول"
            />
          </div>
        )}

        <div className="bg-white rounded-3xl shadow p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                📋 قائمة المحلات
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                عدد النتائج: {filteredStores.length}{showDeleted ? ' — يشمل المعطلة' : ''}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center text-gray-600 py-10">
              جاري تحميل المحلات...
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
              لا توجد محلات مطابقة
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 xl:hidden">
                {filteredStores.map((store) => (
                  <StoreCard
                    key={store.id}
                    store={store}
                    formatNumber={formatNumber}
                    formatDate={formatDate}
                    getStatusInfo={getStatusInfo}
                    onDetails={() => setSelectedStore(store)}
                    onEdit={() => openEdit(store)}
                    onStatement={() => openStatement(store)}
                    onDelete={() => handleDelete(store)}
                    onRestore={() => handleRestore(store)}
                    deleting={deletingId === store.id}
                    restoring={restoringId === store.id}
                  />
                ))}
              </div>

              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-right">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-gray-600">اسم المحل</th>
                      <th className="px-4 py-3 text-gray-600">الهاتف</th>
                      <th className="px-4 py-3 text-gray-600">المالك</th>
                      <th className="px-4 py-3 text-gray-600">العنوان</th>
                      <th className="px-4 py-3 text-gray-600">الرصيد/الدين</th>
                      <th className="px-4 py-3 text-gray-600">الحالة</th>
                      <th className="px-4 py-3 text-gray-600">التاريخ</th>
                      <th className="px-4 py-3 text-gray-600">إجراءات</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredStores.map((store) => {
                      const status = getStatusInfo(store.deleted_at ? 'deleted' : store.status);

                      return (
                        <tr key={store.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-4 font-bold text-gray-900">{store.name}</td>
                          <td className="px-4 py-4 text-gray-600">{store.phone || '-'}</td>
                          <td className="px-4 py-4 text-gray-600">
                            <div>{store.owner_name || '-'}</div>
                            <div className="text-xs text-gray-400">{store.owner_phone || ''}</div>
                          </td>
                          <td className="px-4 py-4 text-gray-600">{store.address || store.map_address || '-'}</td>
                          <td className="px-4 py-4 font-bold text-red-600">{formatNumber(store.total_debt || store.balance || 0)}</td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border ${status.className}`}>
                              <span>{status.icon}</span>
                              <span>{status.label}</span>
                            </span>
                          </td>
                          <td className="px-4 py-4 text-gray-500 text-sm">{formatDate(store.created_at)}</td>
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => setSelectedStore(store)} className="bg-gray-800 hover:bg-black text-white px-3 py-2 rounded-lg text-sm">تفاصيل</button>
                              <button onClick={() => openStatement(store)} className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg text-sm">كشف</button>
                              <button onClick={() => openEdit(store)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm">تعديل</button>

                              {(store.deleted_at || store.status === 'deleted') ? (
                                <button
                                  onClick={() => handleRestore(store)}
                                  disabled={restoringId === store.id}
                                  className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-3 py-2 rounded-lg text-sm"
                                >
                                  {restoringId === store.id ? '...' : 'استرجاع'}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleDelete(store)}
                                  disabled={deletingId === store.id}
                                  className="bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white px-3 py-2 rounded-lg text-sm"
                                >
                                  {deletingId === store.id ? '...' : 'تعطيل'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedStore && (
        <StoreDetailsModal
          store={selectedStore}
          onClose={() => setSelectedStore(null)}
          formatNumber={formatNumber}
          formatDate={formatDate}
          getStatusInfo={getStatusInfo}
          onEdit={() => openEdit(selectedStore)}
          onStatement={() => openStatement(selectedStore)}
          onRestore={() => handleRestore(selectedStore)}
        />
      )}

      {editingStore && (
        <EditStoreModal
          form={editForm}
          onChange={handleEditChange}
          onSubmit={handleEditSubmit}
          onClose={() => setEditingStore(null)}
          saving={editSaving}
        />
      )}

      {statementStore && (
        <StatementModal
          store={statementStore}
          statement={statement}
          from={statementFrom}
          to={statementTo}
          setFrom={setStatementFrom}
          setTo={setStatementTo}
          loading={statementLoading}
          onApply={() => fetchStatement(statementStore.id)}
          onClose={closeStatement}
          formatNumber={formatNumber}
          formatDate={formatDate}
          getTypeIcon={getTypeIcon}
          getTypeLabel={getTypeLabel}
        />
      )}
    </div>
  );
}

function StoreForm({ form, onChange, onSubmit, saving, submitLabel, onCancel, cancelLabel }) {
  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <Field label="اسم المحل" name="name" value={form.name} onChange={onChange} placeholder="مثال: محل النور" required />
      <Field label="رقم هاتف المحل" name="phone" value={form.phone} onChange={onChange} placeholder="0910000000" />
      <Field label="اسم مالك المحل" name="owner_name" value={form.owner_name} onChange={onChange} placeholder="اختياري" />
      <Field label="رقم مالك المحل" name="owner_phone" value={form.owner_phone} onChange={onChange} placeholder="اختياري" />
      <Field label="العنوان" name="address" value={form.address} onChange={onChange} placeholder="طرابلس" />
      <Field label="عنوان الخريطة / الموقع" name="map_address" value={form.map_address} onChange={onChange} placeholder="رابط خرائط أو وصف الموقع" />

      <div>
        <label className="block text-gray-700 font-bold mb-2">حالة المحل</label>
        <select
          name="status"
          value={form.status}
          onChange={onChange}
          className="w-full border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50"
        >
          <option value="active">نشط</option>
          <option value="follow_up">يحتاج متابعة</option>
          <option value="paused">متوقف مؤقتاً</option>
          <option value="deleted">محذوف</option>
        </select>
      </div>

      <div className="md:col-span-2 xl:col-span-4">
        <label className="block text-gray-700 font-bold mb-2">ملاحظات داخلية</label>
        <textarea
          name="notes"
          value={form.notes}
          onChange={onChange}
          placeholder="مثال: لا يرد، يحتاج متابعة، طلب تأجيل، مشكلة في الدفع..."
          rows="3"
          className="w-full border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50 resize-none"
        />
      </div>

      <div className="md:col-span-2 xl:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="submit"
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold py-4 rounded-2xl transition"
        >
          {saving ? 'جاري الحفظ...' : submitLabel}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 font-bold py-4 rounded-2xl transition"
        >
          {cancelLabel}
        </button>
      </div>
    </form>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white/10 border border-white/10 rounded-2xl p-3 min-w-[120px]">
      <p className="text-orange-100 text-xs">{label}</p>
      <p className="text-white font-bold text-xl mt-1">{value}</p>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, required = false }) {
  return (
    <div>
      <label className="block text-gray-700 font-bold mb-2">
        {label}
        {required && <span className="text-red-500 mr-1">*</span>}
      </label>
      <input
        type="text"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50"
      />
    </div>
  );
}

function StoreCard({
  store,
  formatNumber,
  formatDate,
  getStatusInfo,
  onDetails,
  onEdit,
  onStatement,
  onDelete,
  onRestore,
  deleting,
  restoring
}) {
  const status = getStatusInfo(store.deleted_at ? 'deleted' : store.status);

  return (
    <div className="border border-gray-100 bg-gray-50 rounded-3xl p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-xl font-bold text-gray-900">{store.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{store.phone || 'لا يوجد هاتف'}</p>
        </div>

        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs border ${status.className}`}>
          <span>{status.icon}</span>
          <span>{status.label}</span>
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <Info label="المالك" value={store.owner_name || '-'} />
        <Info label="هاتف المالك" value={store.owner_phone || '-'} />
        <Info label="العنوان" value={store.address || store.map_address || '-'} />
        <Info label="الرصيد/الدين" value={formatNumber(store.total_debt || store.balance || 0)} danger />
      </div>

      {store.notes && (
        <div className="bg-white border border-gray-100 rounded-2xl p-3 mb-4">
          <p className="text-xs text-gray-500 mb-1">ملاحظات</p>
          <p className="text-sm text-gray-700">{store.notes}</p>
        </div>
      )}

      <p className="text-xs text-gray-400 mb-4">تاريخ الإنشاء: {formatDate(store.created_at)}</p>

      <div className="grid grid-cols-2 gap-3">
        <ActionButton onClick={onDetails} className="bg-gray-800 hover:bg-black">تفاصيل</ActionButton>
        <ActionButton onClick={onStatement} className="bg-orange-600 hover:bg-orange-700">كشف حساب</ActionButton>
        <ActionButton onClick={onEdit} className="bg-blue-600 hover:bg-blue-700">تعديل</ActionButton>

        {(store.deleted_at || store.status === 'deleted') ? (
          <ActionButton onClick={onRestore} disabled={restoring} className="bg-green-600 hover:bg-green-700 disabled:bg-green-300">
            {restoring ? 'جاري...' : 'استرجاع'}
          </ActionButton>
        ) : (
          <ActionButton onClick={onDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600 disabled:bg-red-300">
            {deleting ? 'جاري...' : 'تعطيل'}
          </ActionButton>
        )}
      </div>
    </div>
  );
}

function ActionButton({ children, onClick, disabled, className }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${className} text-white font-bold py-3 rounded-2xl`}
    >
      {children}
    </button>
  );
}

function Info({ label, value, danger = false }) {
  return (
    <div className="bg-white rounded-2xl p-3 border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`font-bold truncate ${danger ? 'text-red-600' : 'text-gray-800'}`}>{value}</p>
    </div>
  );
}

function StoreDetailsModal({ store, onClose, formatNumber, formatDate, getStatusInfo, onEdit, onStatement, onRestore }) {
  const status = getStatusInfo(store.deleted_at ? 'deleted' : store.status);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" dir="rtl">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 md:p-6 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🏪 تفاصيل المحل</h2>
            <p className="text-gray-500 text-sm mt-1">{store.name}</p>
          </div>

          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl px-4 py-2 font-bold">
            إغلاق
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${status.className}`}>
              <span>{status.icon}</span>
              <span>{status.label}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Detail label="اسم المحل" value={store.name} />
            <Detail label="رقم هاتف المحل" value={store.phone || '-'} />
            <Detail label="اسم المالك" value={store.owner_name || '-'} />
            <Detail label="رقم هاتف المالك" value={store.owner_phone || '-'} />
            <Detail label="العنوان" value={store.address || '-'} />
            <Detail label="عنوان الخريطة / الموقع" value={store.map_address || '-'} />
            <Detail label="الرصيد" value={formatNumber(store.balance || 0)} />
            <Detail label="الدين / المستحق" value={formatNumber(store.total_debt || store.debt || 0)} />
            <Detail label="تاريخ الإنشاء" value={formatDate(store.created_at)} />
            <Detail label="آخر تحديث" value={formatDate(store.updated_at)} />
          </div>

          <div className="mt-5 bg-gray-50 border border-gray-100 rounded-2xl p-4">
            <p className="text-gray-500 text-sm mb-2">ملاحظات داخلية</p>
            <p className="text-gray-800 whitespace-pre-wrap">{store.notes || 'لا توجد ملاحظات'}</p>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={onStatement} className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 rounded-2xl">
              📄 كشف حساب المحل
            </button>

            <button onClick={onEdit} className="bg-gray-800 hover:bg-black text-white font-bold py-4 rounded-2xl">
              ✏️ تعديل البيانات
            </button>

            {(store.deleted_at || store.status === 'deleted') && (
              <button onClick={onRestore} className="md:col-span-2 bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-2xl">
                ♻️ استرجاع المحل
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditStoreModal({ form, onChange, onSubmit, onClose, saving }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" dir="rtl">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 md:p-6 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">✏️ تعديل بيانات المحل</h2>
            <p className="text-gray-500 text-sm mt-1">عدّل البيانات ثم اضغط حفظ</p>
          </div>

          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl px-4 py-2 font-bold">
            إغلاق
          </button>
        </div>

        <div className="p-5 md:p-6">
          <StoreForm
            form={form}
            onChange={onChange}
            onSubmit={onSubmit}
            saving={saving}
            submitLabel="✅ حفظ التعديل"
            onCancel={onClose}
            cancelLabel="إلغاء"
          />
        </div>
      </div>
    </div>
  );
}

function StatementModal({
  store,
  statement,
  from,
  to,
  setFrom,
  setTo,
  loading,
  onApply,
  onClose,
  formatNumber,
  formatDate,
  getTypeIcon,
  getTypeLabel
}) {
  const summary = statement?.summary || {};
  const transactions = statement?.transactions || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" dir="rtl">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-5xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 md:p-6 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">📄 كشف حساب المحل</h2>
            <p className="text-gray-500 text-sm mt-1">{store.name}</p>
          </div>

          <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl px-4 py-2 font-bold">
            إغلاق
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50"
            />

            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border-2 border-gray-200 rounded-2xl p-3 outline-none focus:border-orange-500 bg-gray-50"
            />

            <button onClick={onApply} disabled={loading} className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold py-3 rounded-2xl">
              {loading ? 'جاري...' : 'تطبيق الفترة'}
            </button>

            <button onClick={() => window.print()} className="bg-gray-800 hover:bg-black text-white font-bold py-3 rounded-2xl">
              طباعة
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            <Detail label="رصيد المحل الحالي" value={formatNumber(store.balance || 0)} />
            <Detail label="الدين الحالي" value={formatNumber(store.total_debt || store.debt || 0)} />
            <Detail label="إجمالي الشحن" value={formatNumber(summary.total_recharge || 0)} />
            <Detail label="إجمالي القبض" value={formatNumber(summary.total_collection || 0)} />
          </div>

          {loading ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
              جاري تحميل كشف الحساب...
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
              لا توجد عمليات في هذه الفترة
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
                {transactions.map((tx) => (
                  <div key={tx.id} className="bg-gray-50 border border-gray-100 rounded-3xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="font-bold text-gray-900">{getTypeIcon(tx.type)} {getTypeLabel(tx.type)}</p>
                      <p className="text-xl font-bold text-green-700">{formatNumber(tx.amount)}</p>
                    </div>

                    <p className="text-sm text-gray-600 mb-3">{tx.description || '-'}</p>

                    <div className="grid grid-cols-2 gap-3">
                      <Info label="المحفظة" value={tx.wallet_name || '-'} />
                      <Info label="المستخدم" value={tx.user_name || '-'} />
                      <div className="col-span-2">
                        <Info label="التاريخ" value={formatDate(tx.created_at)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-right text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-gray-600">النوع</th>
                      <th className="px-4 py-3 text-gray-600">المبلغ</th>
                      <th className="px-4 py-3 text-gray-600">المحفظة</th>
                      <th className="px-4 py-3 text-gray-600">المستخدم</th>
                      <th className="px-4 py-3 text-gray-600">الوصف</th>
                      <th className="px-4 py-3 text-gray-600">التاريخ</th>
                    </tr>
                  </thead>

                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-bold">{getTypeIcon(tx.type)} {getTypeLabel(tx.type)}</td>
                        <td className="px-4 py-3 font-bold text-green-700">{formatNumber(tx.amount)}</td>
                        <td className="px-4 py-3">{tx.wallet_name || '-'}</td>
                        <td className="px-4 py-3">{tx.user_name || '-'}</td>
                        <td className="px-4 py-3 max-w-[300px] truncate">{tx.description || '-'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(tx.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <p className="text-gray-900 font-bold break-words">{value}</p>
    </div>
  );
}

export default Stores;
